import React, { useState, useEffect, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Card, Button } from '@/components/ui';
import { getToken, buildApiUrl } from '@/utils';

interface BaileysQRConnectProps {
  shopId: string;
  onConnected?: (phone?: string) => void;
  onError?: (error: string) => void;
  className?: string;
  disabled?: boolean;
}

type ConnectionStatus = 'idle' | 'connecting' | 'qr_ready' | 'connected' | 'error';

interface SSEUpdate {
  type: 'connecting' | 'qr' | 'status' | 'ping' | 'error';
  qr?: string;
  status?: string;
  message?: string;
  error?: string;
  phone?: string;
}

/**
 * Baileys WhatsApp QR Connect Component
 * Desktop-first QR code scanner for connecting WhatsApp via Baileys
 * Streams QR code and connection updates via SSE
 */
export const BaileysQRConnect: React.FC<BaileysQRConnectProps> = ({
  shopId,
  onConnected,
  onError,
  className = '',
  disabled = false,
}) => {
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [connectionTime, setConnectionTime] = useState<Date | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const statusRef = useRef<ConnectionStatus>('idle');

  // Keep statusRef in sync so the SSE error handler has current value
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  /**
   * Start QR code generation stream
   */
  const startConnection = () => {
    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setStatus('connecting');
    setErrorMessage('');
    setQrCode(null);
    setPhoneNumber('');
    setConnectionTime(null);

    // EventSource cannot set custom headers in the browser.
    // We pass the JWT as a ?token= query parameter instead.
    const token = getToken();
    if (!token) {
      setStatus('error');
      setErrorMessage('Not authenticated. Please log in again.');
      onError?.('Not authenticated');
      return;
    }

    const url = buildApiUrl(`shops/${shopId}/connect?token=${encodeURIComponent(token)}`);
    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('message', (event) => {
      try {
        const update: SSEUpdate = JSON.parse(event.data);
        handleSSEUpdate(update);
      } catch (err) {
        console.error('Error parsing SSE message:', err);
      }
    });

    eventSource.addEventListener('error', () => {
      // Only flag as error if we haven't already connected
      if (statusRef.current !== 'connected') {
        console.error('SSE connection error');
        eventSource.close();
        setStatus('error');
        setErrorMessage('Connection lost. Please try again.');
        onError?.('Connection lost');
      }
    });
  };

  /**
   * Handle SSE update from backend
   */
  const handleSSEUpdate = (update: SSEUpdate) => {
    switch (update.type) {
      case 'connecting':
        setStatus('connecting');
        setErrorMessage('');
        break;

      case 'qr':
        if (update.qr) {
          setQrCode(update.qr);
          setStatus('qr_ready');
          setErrorMessage('');
        }
        break;

      case 'status':
        if (update.status === 'connected') {
          setStatus('connected');
          setPhoneNumber(update.phone || '');
          setConnectionTime(new Date());
          setErrorMessage('');

          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }

          onConnected?.(update.phone);
        }
        break;

      case 'error':
        setStatus('error');
        setErrorMessage(update.message || 'Connection error');
        onError?.(update.message || 'Connection error');
        break;

      case 'ping':
        // Keep-alive, ignore
        break;
    }
  };

  /**
   * Disconnect WhatsApp
   */
  const disconnect = async () => {
    try {
      const token = getToken();
      const response = await fetch(buildApiUrl(`shops/${shopId}/disconnect`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (response.ok) {
        setStatus('idle');
        setPhoneNumber('');
        setConnectionTime(null);
        setQrCode(null);
      } else {
        throw new Error(`Disconnect failed: ${response.status}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Disconnect failed';
      setErrorMessage(message);
      onError?.(message);
    }
  };

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-950">Connect WhatsApp</h2>
        <p className="mt-1 text-sm text-slate-600">
          Scan the QR code with your WhatsApp phone to connect this shop
        </p>
      </div>

      {/* Idle State - Start Connection */}
      {status === 'idle' && (
        <Card className="p-8 text-center">
          <div className="mb-6">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg 
                className="w-8 h-8 text-[#25D366]" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.487-1.761-1.66-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
            </div>
          </div>
          <p className="mb-6 text-slate-600">
            Click the button below to generate a QR code and connect your WhatsApp account
          </p>
          <Button
            onClick={startConnection}
            className="w-full"
            size="lg"
            disabled={disabled}
          >
            📱 Generate QR Code
          </Button>
          {disabled && (
            <p className="mt-2 text-xs font-semibold text-slate-500 text-center">
              🔒 Accessed by owner
            </p>
          )}
        </Card>
      )}

      {/* Connecting State */}
      {status === 'connecting' && (
        <Card className="p-8 text-center">
          <div className="mb-6 flex justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600"></div>
          </div>
          <p className="text-slate-600">Initializing WhatsApp connection...</p>
          <p className="mt-2 text-sm text-slate-500">Please wait while we set up your QR code</p>
        </Card>
      )}

      {/* QR Ready State - Show QR Code */}
      {status === 'qr_ready' && qrCode && (
        <Card className="p-8">
          <div className="space-y-6">
            <div className="text-center">
              <p className="mb-4 text-sm font-medium text-slate-700">
                📱 Open WhatsApp on your phone and scan this QR code
              </p>

              {/* QR Code Container */}
              <div className="flex justify-center rounded-lg bg-slate-50 p-6">
                <div className="rounded-lg bg-white p-4 shadow-sm">
                  <QRCodeCanvas
                    value={qrCode}
                    size={300}
                    level="H"
                    includeMargin={true}
                  />
                </div>
              </div>

              <p className="mt-4 text-xs text-slate-500">
                QR code will refresh automatically if it expires
              </p>
            </div>

            {/* Connection Status Indicator */}
            <div className="flex items-center justify-center gap-2 rounded-lg bg-blue-50 px-4 py-3">
              <div className="h-2 w-2 animate-pulse rounded-full bg-blue-600"></div>
              <span className="text-sm font-medium text-blue-900">Waiting for scan...</span>
            </div>
          </div>
        </Card>
      )}

      {/* Connected State */}
      {status === 'connected' && (
        <Card className="border-l-4 border-l-green-500 bg-green-50 p-8">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">✓</div>
              <div>
                <h3 className="font-semibold text-green-900">WhatsApp Connected</h3>
                <p className="mt-1 text-sm text-green-700">
                  Your WhatsApp account is ready to receive print orders
                </p>
              </div>
            </div>

            {phoneNumber && (
              <div className="rounded bg-white px-3 py-2">
                <p className="text-xs text-slate-500">Connected Phone</p>
                <p className="font-mono font-semibold text-slate-900">{phoneNumber}</p>
              </div>
            )}

            {connectionTime && (
              <p className="text-xs text-slate-500">
                Connected at {connectionTime.toLocaleTimeString()}
              </p>
            )}

            <Button
              variant="secondary"
              onClick={disconnect}
              className="w-full"
              size="sm"
              disabled={disabled}
            >
              Disconnect WhatsApp
            </Button>
            {disabled && (
              <p className="mt-2 text-xs font-semibold text-slate-500 text-center">
                🔒 Accessed by owner
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Error State */}
      {status === 'error' && (
        <Card className="border-l-4 border-l-red-500 bg-red-50 p-8">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">⚠️</div>
              <div>
                <h3 className="font-semibold text-red-900">Connection Failed</h3>
                <p className="mt-1 text-sm text-red-700">{errorMessage}</p>
              </div>
            </div>

            <Button
              onClick={startConnection}
              className="w-full"
              size="sm"
              disabled={disabled}
            >
              Try Again
            </Button>
            {disabled && (
              <p className="mt-2 text-xs font-semibold text-slate-500 text-center">
                🔒 Accessed by owner
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Troubleshooting Help */}
      {(status === 'qr_ready' || status === 'connecting') && (
        <Card className="bg-slate-50 p-4">
          <details>
            <summary className="cursor-pointer font-medium text-slate-900">
              Need help? 🤔
            </summary>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li>• Make sure WhatsApp is installed on your phone</li>
              <li>• Keep this screen open while scanning</li>
              <li>• QR code will refresh if it takes too long</li>
              <li>• Your phone must have internet connection</li>
            </ul>
          </details>
        </Card>
      )}
    </div>
  );
};

export default BaileysQRConnect;
